<template>
  <div class="d-flex flex-column fill-height" style="position:relative;">

    <!-- Loading overlay -->
    <div v-if="loading" class="d-flex align-center justify-center" style="position:absolute;inset:0;z-index:5;background:rgba(0,0,0,0.5);">
      <v-progress-circular indeterminate color="primary" size="48" />
    </div>

    <!-- Order header -->
    <v-toolbar flat dense class="flex-grow-0">
      <div>
        <div class="text-overline" style="line-height:1.2">Bestellung</div>
        <div class="text-subtitle-2">#{{ orderId }}</div>
      </div>
      <div class="ml-6" v-if="orderData && orderData.line_items">
        <div class="text-overline" style="line-height:1.2">Positionen</div>
        <div class="text-subtitle-2">{{ orderData.line_items.length }}</div>
      </div>
      <v-spacer />
      <v-btn icon small @click="$emit('close')">
        <v-icon small>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>

    <!-- Step tabs -->
    <v-tabs v-model="tab" grow class="flex-grow-0">
      <v-tab>
        <v-icon small left>mdi-package-variant-closed</v-icon>
        Pack
      </v-tab>
      <v-tab>
        <v-icon small left>mdi-truck-outline</v-icon>
        Ship
      </v-tab>
    </v-tabs>

    <!-- Pack tab -->
    <template v-if="tab === 0">
      <div class="flex-grow-1 overflow-y-auto pa-3">

        <!-- Shipment cards -->
        <v-card
          v-for="(shipment, si) in shipments"
          :key="'s-' + si"
          outlined
          class="mb-3"
        >
          <v-card-title class="text-body-2 font-weight-bold pa-3 pb-0 d-flex align-center">
            <v-icon small class="mr-2">mdi-package-variant</v-icon>
            Shipment {{ si + 1 }}
            <v-spacer />
            <v-btn icon x-small @click="removeShipment(si)">
              <v-icon x-small>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-list dense class="pa-0">
            <template v-for="item in shipment.items">
              <v-list-item
                v-if="getAvailable(item.sku, si) > 0 || item.qty > 0"
                :key="item.sku"
                class="px-3"
                style="min-height:40px;"
              >
                <!-- Desktop: name as separate column -->
                <v-list-item-content v-if="$vuetify.breakpoint.mdAndUp" class="py-1" style="flex:1;min-width:0;">
                  <v-list-item-title class="text-caption font-weight-medium text-truncate">{{ item.internal_name || item.name }}</v-list-item-title>
                </v-list-item-content>

                <div class="d-flex align-center" :class="{ 'ml-2': $vuetify.breakpoint.mdAndUp }" :style="$vuetify.breakpoint.smAndDown ? 'width:100%' : ''" style="gap:2px;">
                  <v-btn tile depressed x-small :disabled="item.qty <= 0" style="min-width:32px;height:28px;" @click="setShipmentQty(si, item.sku, item.qty - 1)">
                    <v-icon x-small>mdi-minus</v-icon>
                  </v-btn>
                  <v-sheet
                    class="d-flex align-center justify-center text-caption font-weight-bold"
                    :class="{ 'flex-grow-1': $vuetify.breakpoint.smAndDown }"
                    :color="item.qty >= getAvailable(item.sku, si) ? 'success' : item.qty > 0 ? 'warning' : 'grey lighten-2'"
                    :dark="item.qty > 0"
                    style="height:28px;min-width:52px;border-radius:2px;"
                  >
                    <!-- Mobile: name inside the counter -->
                    <span v-if="$vuetify.breakpoint.smAndDown" class="text-truncate px-2">
                      {{ item.internal_name || item.name }} &middot; {{ item.qty }}/{{ getAvailable(item.sku, si) }}
                    </span>
                    <span v-else>{{ item.qty }} / {{ getAvailable(item.sku, si) }}</span>
                  </v-sheet>
                  <v-btn tile depressed x-small :disabled="item.qty >= getAvailable(item.sku, si)" style="min-width:32px;height:28px;" @click="setShipmentQty(si, item.sku, item.qty + 1)">
                    <v-icon x-small>mdi-plus</v-icon>
                  </v-btn>
                </div>
              </v-list-item>
            </template>
          </v-list>


          <!-- Shipment summary -->
          <v-card-text class="pa-3 pt-0 text-caption d-flex align-center" style="opacity:0.6;">
            <v-icon x-small class="mr-1">mdi-package-variant-closed</v-icon>
            {{ shipmentItemCount(si) }} Artikel in diesem Shipment
          </v-card-text>
        </v-card>

        <!-- Add shipment button -->
        <v-btn
          v-if="hasUnpackedItems"
          outlined block large
          @click="addShipment"
        >
          <v-icon left>mdi-plus</v-icon>
          Shipment {{ shipments.length + 1 }} erstellen
        </v-btn>

        <!-- All packed info -->
        <div v-if="allItemsPacked && shipments.length" class="text-center pa-4">
          <v-icon color="success" size="32">mdi-check-circle</v-icon>
          <div class="text-body-2 font-weight-bold mt-2">Alle Artikel verteilt</div>
        </div>

      </div>

      <v-footer color="transparent" class="pa-3 flex-shrink-0">
        <v-btn
          color="primary" depressed block large
          :disabled="!shipments.length || totalPackedCount === 0"
          @click="tab = 1"
        >
          <v-icon left>mdi-truck-outline</v-icon>
          Weiter zu Ship
        </v-btn>
      </v-footer>
    </template>

    <!-- Ship tab -->
    <template v-if="tab === 1">
      <div class="flex-grow-1 d-flex flex-column overflow-hidden">

        <div class="pa-3 flex-shrink-0">
          <v-text-field
            v-model="tracking_buffer"
            outlined dense
            hide-details="auto"
            placeholder="Tracking-Code scannen..."
            prepend-inner-icon="mdi-truck-check-outline"
            inputmode="none"
            autocomplete="off"
            @keydown.enter.prevent="onTrackingScan"
          />
        </div>

        <v-list v-if="tracking_codes.length" dense class="pa-0 flex-shrink-0">
          <v-subheader>Tracking-Codes</v-subheader>
          <v-list-item v-for="(code, i) in tracking_codes" :key="'t-' + i">
            <v-list-item-icon class="mr-3">
              <v-icon small color="primary">mdi-truck-check-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="text-body-2" style="font-family:monospace;">{{ code }}</v-list-item-title>
            </v-list-item-content>
            <v-list-item-action class="my-0">
              <v-btn icon x-small @click="tracking_codes.splice(i, 1)">
                <v-icon x-small>mdi-close</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>

        <v-divider v-if="tracking_codes.length" class="flex-shrink-0" />

        <image-capture
          ref="imageCapture"
          :value="photos"
          @input="$emit('update:photos', $event)"
          autostart
          class="flex-grow-1"
        />
      </div>

      <v-footer color="transparent" class="pa-3 flex-shrink-0">
        <v-btn
          color="success" depressed block large
          :loading="isUploading"
          :disabled="!photos.length && !tracking_codes.length"
          @click="$emit('upload')"
        >
          <v-icon left>mdi-cloud-upload</v-icon>
          <template v-if="photos.length">
            {{ photos.length }} Foto{{ photos.length !== 1 ? 's' : '' }} hochladen
          </template>
          <template v-else>
            Versand bestätigen
          </template>
        </v-btn>
      </v-footer>
    </template>

  </div>
</template>

<script>
import ImageCapture from '@/components/_scanner/ImageCapture.vue'

export default {
  name: 'OrderDetailPanel',

  components: { ImageCapture },

  props: {
    orderId: { type: [Number, String], default: null },
    orderData: { type: Object, default: null },
    photos: { type: Array, default: () => [] },
    isUploading: { type: Boolean, default: false },
    uploadStatus: { type: String, default: null },
    loading: { type: Boolean, default: false },
  },

  data() {
    return {
      tab: 0,
      shipments: [],
      tracking_buffer: '',
      tracking_codes: [],
    }
  },

  computed: {
    allPickslipItems() {
      return (this.orderData?.pickslip_items || []).map((pi, i) => ({
        sku: pi.sku,
        name: pi.title || pi.sku,
        internal_name: pi.internal_name || null,
        thumbnail: pi.thumbnail || null,
        ordered: Number(pi.amount) || 0,
      }))
    },

    packedBySku() {
      const totals = {}
      for (const shipment of this.shipments) {
        for (const item of shipment.items) {
          totals[item.sku] = (totals[item.sku] || 0) + item.qty
        }
      }
      return totals
    },

    hasUnpackedItems() {
      return this.allPickslipItems.some(item => {
        const packed = this.packedBySku[item.sku] || 0
        return packed < item.ordered
      })
    },

    allItemsPacked() {
      if (!this.allPickslipItems.length) return false
      return this.allPickslipItems.every(item => {
        const packed = this.packedBySku[item.sku] || 0
        return packed >= item.ordered
      })
    },

    totalPackedCount() {
      return Object.values(this.packedBySku).reduce((sum, v) => sum + v, 0)
    },
  },

  watch: {
    orderId() {
      this.tab = 0
      this.shipments = []
      this.tracking_codes = []
      this.tracking_buffer = ''
    },
  },

  methods: {
    addShipment() {
      const items = this.allPickslipItems.map(pi => ({
        sku: pi.sku,
        name: pi.name,
        internal_name: pi.internal_name,
        thumbnail: pi.thumbnail,
        qty: 0,
      }))
      this.shipments.push({ items })
    },

    removeShipment(index) {
      this.shipments.splice(index, 1)
    },

    getAvailable(sku, excludeShipmentIndex) {
      const item = this.allPickslipItems.find(i => i.sku === sku)
      if (!item) return 0

      let packedElsewhere = 0
      this.shipments.forEach((shipment, si) => {
        if (si === excludeShipmentIndex) return
        const found = shipment.items.find(i => i.sku === sku)
        if (found) packedElsewhere += found.qty
      })

      return item.ordered - packedElsewhere
    },

    setShipmentQty(shipmentIndex, sku, value) {
      const item = this.shipments[shipmentIndex].items.find(i => i.sku === sku)
      if (!item) return
      const max = this.getAvailable(sku, shipmentIndex)
      this.$set(item, 'qty', Math.max(0, Math.min(value, max)))
    },

    shipmentItemCount(shipmentIndex) {
      return this.shipments[shipmentIndex].items.reduce((sum, i) => sum + i.qty, 0)
    },

    onTrackingScan() {
      const code = this.tracking_buffer.trim()
      this.tracking_buffer = ''
      if (!code) return
      this.tracking_codes.push(code)
    },
  },

  beforeDestroy() {
    if (this.$refs.imageCapture) this.$refs.imageCapture.stopCamera()
  },
}
</script>
